import { JOBS_ANALYTICS } from '@/utils/constants';
import { useCurrentUserStore } from '@/services/auth/store';
import { usePostHog } from 'posthog-js/react';
import type { BoardViewerState } from '@/services/jobs/job-board-viewer';

type FilterStateParam = Record<string, unknown>;

export type JobReferShareNetwork = 'linkedin' | 'x' | 'copy_link';

/**
 * Which surface a role was acted on from. The same role card now renders on the job
 * board, on a team profile and in the home feed's For You roll-ups, and without this
 * they are indistinguishable in PostHog — which would make it impossible to tell
 * whether the team-profile section or the feed drives any clicks or referrals at all.
 *
 * Required, never defaulted: a default would silently mislabel the next surface that
 * forgets to pass it.
 */
export type JobSurface = 'job-board' | 'team-profile' | 'home-feed';

export type JobReferBaseParams = {
  job_id: string;
  team_id: string;
  team_name: string;
  role_title: string;
  role_category: string | null;
  seniority: string | null;
  source: JobSurface;
  uses_team_refer_email?: boolean;
  /**
   * Which kind of referral the event is about. Optional because the click and
   * modal-open events fire before anyone has been chosen.
   *
   * It exists because `referred_member_uid` cannot tell the two apart: someone outside
   * the network has no uid, so that field is `''` for them, and `''` would otherwise be
   * indistinguishable from a bug. Nothing about who they are goes any further than this
   * — see the payload rule on `JobApplyBaseParams` below, which holds here for the same
   * reason: a dashboard is a wider audience than a referral email.
   */
  referee_type?: 'network_member' | 'outside_network';
};

/**
 * Which control started an apply-funnel step: a role row, the board banner, the
 * header — or `resume`, which is the flow picking itself back up after the
 * Privy round trip rather than anything the person pressed. Kept distinct so
 * apply-click counts stay a count of actual clicks.
 */
/** `detail` is the drawer's own footer — the press that used to live on the row. */
export type JobApplyTrigger = 'row' | 'banner' | 'header' | 'resume' | 'detail';

/**
 * Apply-funnel payloads carry ONLY what's listed here: uids, viewer state,
 * source/trigger, and a failure category. Never form field values (email,
 * name, linkedin), never the jobSearchStatus value (PL-Team-only — a PostHog
 * dashboard is a wider audience than that), never cover-letter text.
 * `captureEvent` already stamps the logged-in user's identity; add nothing.
 *
 * `job_id`/`team_id` are null for flows started without a role (banner/header
 * sign-up, generic drawer edits).
 */
export type JobApplyBaseParams = {
  job_id: string | null;
  team_id: string | null;
  viewer_state: BoardViewerState;
  source: JobSurface;
};

export const useJobsAnalytics = () => {
  const postHog = usePostHog();

  const captureEvent = (eventName: string, eventParams: Record<string, unknown> = {}) => {
    try {
      if (!postHog?.capture) return;
      const userInfo = useCurrentUserStore.getState().currentUser;
      const loggedInUserUid = userInfo?.uid;
      const loggedInUserEmail = userInfo?.email;
      const loggedInUserName = userInfo?.name;
      const is_authenticated = Boolean(loggedInUserUid);
      postHog.capture(eventName, {
        ...eventParams,
        is_authenticated,
        loggedInUserUid,
        loggedInUserEmail,
        loggedInUserName,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const onJobsPageViewed = (args: { result_count: number; filter_state: FilterStateParam }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOBS_PAGE_VIEWED, { ...args });
  };

  const onJobsFiltersApplied = (args: {
    filter_type: string;
    filter_value: unknown;
    result_count: number;
    filter_state: FilterStateParam;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOBS_FILTERS_APPLIED, { ...args });
  };

  const onJobsFiltersCleared = (args: { result_count: number; filter_state: FilterStateParam }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOBS_FILTERS_CLEARED, { ...args });
  };

  const onJobsSearched = (args: { search_query: string; result_count: number; filter_state: FilterStateParam }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOBS_SEARCHED, { ...args });
  };

  const onJobsSortChanged = (args: { sort_key: string; result_count: number }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOBS_SORT_CHANGED, { ...args });
  };

  const onJobClicked = (args: {
    job_id: string;
    team_id: string;
    team_name: string;
    role_title: string;
    role_category: string | null;
    seniority: string | null;
    focus_areas: string[];
    position_in_list: number;
    source: JobSurface;
    /** Board-only: a team profile has no filters, so it sends nothing rather than `{}`. */
    filter_state?: FilterStateParam;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_CLICKED, { ...args });
  };

  const onJobAlertCtaViewed = (args: {
    cta_variant: 'banner' | 'empty';
    filter_state: FilterStateParam;
    result_count: number;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_CTA_VIEWED, { ...args });
  };

  const onJobAlertCtaClicked = (args: { cta_variant: 'banner' | 'empty'; filter_state: FilterStateParam }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_CTA_CLICKED, { ...args });
  };

  const onJobAlertSet = (args: { alert_id: string; filter_state: FilterStateParam; auth_required: boolean }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_SET, { ...args });
  };

  const onJobAlertUpdated = (args: { alert_id: string; filter_state: FilterStateParam }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_UPDATED, { ...args });
  };

  const onJobAlertConflict = (args: { existing_alert_id: string | null }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_CONFLICT, { ...args });
  };

  const onJobAlertRenamed = (args: { alert_id: string }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_RENAMED, { ...args });
  };

  const onJobAlertDeleted = (args: { alert_id: string }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_DELETED, { ...args });
  };

  const onJobAlertEmailLinkClicked = (args: {
    alert_id: string;
    job_id: string;
    team_id?: string;
    position_in_email?: number;
    utm_source: string;
    utm_code?: string;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ALERT_EMAIL_LINK_CLICKED, { ...args });
  };

  const onJobReferClicked = (args: JobReferBaseParams & { auth_required: boolean }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_CLICKED, { ...args });
  };

  const onJobReferModalOpened = (args: JobReferBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_MODAL_OPENED, { ...args });
  };

  const onJobReferModalCancelled = (
    args: JobReferBaseParams & {
      had_referee: boolean;
      recipient_count: number;
      note_was_edited: boolean;
    },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_MODAL_CANCELLED, { ...args });
  };

  const onJobReferRefereeSelected = (args: JobReferBaseParams & { referred_member_uid: string }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_REFEREE_SELECTED, { ...args });
  };

  const onJobReferRecipientsChanged = (
    args: JobReferBaseParams & { recipient_count: number; has_external_email: boolean },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_RECIPIENTS_CHANGED, { ...args });
  };

  const onJobReferNoteEdited = (args: JobReferBaseParams & { referred_member_uid: string }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_NOTE_EDITED, { ...args });
  };

  const onJobReferNoteReset = (args: JobReferBaseParams & { referred_member_uid: string }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_NOTE_RESET, { ...args });
  };

  /** The refer modal's suggested-leads list rendered. Suggestions are not
   *  preselected, so this is the denominator `onJobReferSuggestedLeadSelected`
   *  needs — without it, a low pick rate can't be told apart from a list nobody
   *  saw. */
  const onJobReferSuggestedLeadsShown = (args: JobReferBaseParams & { suggested_count: number }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SUGGESTED_LEADS_SHOWN, { ...args });
  };

  /** A lead picked from the suggested list rather than found via search — kept
   *  distinct from `onJobReferRefereeSelected` so suggestion adoption can be
   *  read on its own. */
  const onJobReferSuggestedLeadSelected = (args: JobReferBaseParams & { referred_member_uid: string }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SUGGESTED_LEAD_SELECTED, { ...args });
  };

  const onJobReferCcReferredPersonToggled = (
    args: JobReferBaseParams & { referred_member_uid: string; next_state: boolean },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_CC_REFERRED_PERSON_TOGGLED, { ...args });
  };

  /* `copied_referred_member` is the state of the refer modal's "Copy <First> on this
     email" tick. Read it as *intent*, not delivery: until the backend honours
     `includeReferredMember`, it copies the referred member on every referral
     regardless of what this says. */
  const onJobReferSubmitted = (
    args: JobReferBaseParams & {
      referred_member_uid: string;
      recipient_count: number;
      has_external_email: boolean;
      note_was_edited: boolean;
      copied_referred_member: boolean;
    },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SUBMITTED, { ...args });
  };

  const onJobReferSucceeded = (
    args: JobReferBaseParams & {
      referred_member_uid: string;
      recipient_count: number;
      has_external_email: boolean;
      note_was_edited: boolean;
      copied_referred_member: boolean;
      referral_uid?: string;
    },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SUCCEEDED, { ...args });
  };

  const onJobReferFailed = (
    args: JobReferBaseParams & {
      referred_member_uid: string;
      recipient_count: number;
      has_external_email: boolean;
      note_was_edited: boolean;
      copied_referred_member: boolean;
      error_type?: string;
    },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_FAILED, { ...args });
  };

  const onJobReferShareMenuOpened = (args: JobReferBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SHARE_MENU_OPENED, { ...args });
  };

  const onJobReferShared = (args: JobReferBaseParams & { network: JobReferShareNetwork }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SHARED, { ...args });
  };

  /**
   * A `/jobs?job=` link copied or shared from the refer menu was opened.
   *
   * Deliberately its own event rather than a field on `job-detail-opened`,
   * which fires immediately after it: that event is the denominator of the
   * apply funnel, and folding shared arrivals into it would silently change
   * every dashboard already reading it. Join the two on `job_id` instead.
   */
  const onJobReferShareLinkOpened = (args: {
    job_id: string;
    utm_source: string;
    /** The share channel, absent if the link was hand-edited on its way here. */
    utm_medium: string | null;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_REFER_SHARE_LINK_OPENED, { ...args });
  };

  /**
   * A profile link in a referral or application email was opened — the
   * clickthrough that closes the loop on `onJobReferSucceeded` and
   * `onJobApplySubmitted`, neither of which could see past the send.
   *
   * `utm_content` says whose card was pressed, because the referral email
   * carries both the referrer's and the referred person's.
   */
  const onJobEmailProfileLinkClicked = (args: {
    profile_member_uid: string;
    job_id: string | null;
    utm_source: string;
    utm_content: string | null;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_EMAIL_PROFILE_LINK_CLICKED, { ...args });
  };

  const onJobApplyClicked = (args: JobApplyBaseParams & { trigger: JobApplyTrigger }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_CLICKED, { ...args });
  };

  /**
   * Kept on the payload so existing dashboards do not break. The Team email
   * field is gone from sign-up, so this is always false.
   */
  const onJobApplySignUpSubmitted = (
    args: JobApplyBaseParams & { trigger: JobApplyTrigger; has_team_email: boolean },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_SIGNUP_SUBMITTED, { ...args });
  };

  const onJobApplySignUpFailed = (args: JobApplyBaseParams & { failure_category: 'duplicate' | 'request-failed' }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_SIGNUP_FAILED, { ...args });
  };

  /** The dedicated Job Aspirant sign-up/create-profile path, kept apart from
   *  `onJobApplySignUpSubmitted` so its funnel doesn't get pooled with the
   *  standard member sign-up. */
  const onJobAspirantSignUpSubmitted = (args: JobApplyBaseParams & { trigger: JobApplyTrigger }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_ASPIRANT_SIGNUP_SUBMITTED, { ...args });
  };

  /**
   * The reading step opened — the top of the funnel now that Apply sits behind
   * it. Against `ON_JOB_APPLY_CLICKED` this is the number that says whether the
   * extra press is earning its place: opens that never reach an apply are
   * people the row used to send straight into the flow.
   */
  const onJobDetailOpened = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_DETAIL_OPENED, { ...args });
  };

  const onJobApplyDrawerOpened = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_DRAWER_OPENED, { ...args });
  };

  const onJobApplyDrawerSaved = (args: JobApplyBaseParams & { profile_complete: boolean }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_DRAWER_SAVED, { ...args });
  };

  const onJobApplySubmitted = (args: JobApplyBaseParams & { cover_letter_length: number }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_SUBMITTED, { ...args });
  };

  const onJobApplyFailed = (args: JobApplyBaseParams & { failure_category: 'already-applied' | 'request-failed' }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_FAILED, { ...args });
  };

  /**
   * A named step of the unified flow became visible. `job-detail-opened` and
   * `job-apply-drawer-opened` still fire for review and profile; this one is
   * the funnel that also covers application and the role-less sign-up modal.
   */
  const onJobApplyStepViewed = (
    args: JobApplyBaseParams & { step: 'review' | 'profile' | 'application' | 'sign-up' },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_STEP_VIEWED, { ...args });
  };

  /**
   * Closed without submitting. `step` is where they left; skip this when the
   * close is the success path (sign-up posted, profile-only saved).
   */
  const onJobApplyFlowClosed = (
    args: JobApplyBaseParams & {
      step: 'review' | 'profile' | 'application' | 'sign-up';
      cover_letter_started: boolean;
    },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_FLOW_CLOSED, { ...args });
  };

  /**
   * Pending applicant sent to the team's own posting. Without this, apply-clicked
   * with no submit cannot be told apart from abandoning the in-app flow.
   */
  const onJobApplyExternalRedirected = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_EXTERNAL_REDIRECTED, { ...args });
  };

  /**
   * A stranger opened "What your profile unlocks?" under the drawer's footer.
   *
   * The only signal that the case for a profile was read at all. The card in the
   * body of the step is rendered whether or not anyone looks at it; this one
   * takes a deliberate hover or press, which is why it is worth an event where
   * the card is not.
   *
   * `surface` because the popover and the modal are the same content behind very
   * different amounts of intent — hovering a caption on the way to a button is
   * not the same act as tapping it and dismissing a modal to get back.
   */
  const onJobUnlockInfoOpened = (args: JobApplyBaseParams & { surface: 'popover' | 'modal' }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_UNLOCK_INFO_OPENED, { ...args });
  };

  /**
   * The outbound review step's "Create profile" press — into the drawer's own
   * account step, not a submit. Paired with `onJobUnlockInfoOpened`, which
   * sits right above this button: together they answer whether reading "What
   * your profile unlocks?" moves the click, or whether it's read and dropped.
   */
  const onJobCreateProfileClicked = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_CREATE_PROFILE_CLICKED, { ...args });
  };

  const onJobInterestComposerOpened = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_INTEREST_COMPOSER_OPENED, { ...args });
  };

  const onJobInterestComposerCancelled = (args: JobApplyBaseParams & { had_note: boolean }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_INTEREST_COMPOSER_CANCELLED, { ...args });
  };

  /**
   * The light signal beside Apply.
   *
   * `resumed` separates the two ways a mark happens: pressed and recorded on the
   * spot, or pressed while logged out and recorded on the way back from Privy.
   * They convert differently and the funnel is unreadable if they are one number.
   *
   * A logged-out press fires nothing here — it is a sign-up intent, and
   * `onJobApplyClicked` already counts those with `trigger`. The mark event
   * fires when the signal actually exists.
   *
   * `has_note` (LAB-2713): whether a note went with the signal. The note-attach
   * rate LAB-2726's Metrics section reads is marks with `has_note = true` over
   * all marks, so this is set on every mark, the login round-trip included.
   */
  const onJobInterestMarked = (args: JobApplyBaseParams & { resumed: boolean; has_note: boolean }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_INTEREST_MARKED, { ...args });
  };

  const onJobInterestUndone = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_INTEREST_UNDONE, { ...args });
  };

  const onJobInterestFailed = (
    args: JobApplyBaseParams & { action: 'mark' | 'undo'; failure_category: 'gone' | 'request-failed' },
  ) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_INTEREST_FAILED, { ...args });
  };

  /**
   * The open-role signal: interest in a team, sent from its card when none of
   * its postings fit.
   *
   * No `job_id` in the payload at all, rather than a null one — there is no role
   * involved, and a null would read as "we failed to capture it".
   *
   * Like the role signal, a logged-out press fires nothing: it is a sign-up
   * intent, and the mark event fires when the signal actually exists.
   */
  const onTeamInterestMarked = (args: { team_id: string; viewer_state: BoardViewerState; source: JobSurface }) => {
    captureEvent(JOBS_ANALYTICS.ON_TEAM_INTEREST_MARKED, { ...args });
  };

  const onTeamInterestFailed = (args: {
    team_id: string;
    viewer_state: BoardViewerState;
    source: JobSurface;
    /** `gone` is a 404 — the team uid is unknown to the server. */
    failure_category: 'gone' | 'request-failed';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_TEAM_INTEREST_FAILED, { ...args });
  };

  const onJobApplyFollowDeclined = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_FOLLOW_DECLINED, { ...args });
  };

  const onJobInterestFollowDeclined = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_INTEREST_FOLLOW_DECLINED, { ...args });
  };

  const onJobApplyFollowFailed = (args: JobApplyBaseParams) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLY_FOLLOW_FAILED, { ...args });
  };

  const onJobHiringViewed = (args: { team_id: string; job_id: string | null }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_HIRING_VIEWED, { ...args });
  };

  const onJobApplicantOpened = (args: { team_id: string; job_id: string | null; kind: 'application' | 'interest' }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLICANT_OPENED, { ...args });
  };

  const onJobApplicantReviewed = (args: {
    team_id: string;
    job_id: string | null;
    kind: 'application' | 'interest';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLICANT_REVIEWED, { ...args });
  };

  const onJobApplicantReviewUndone = (args: {
    team_id: string;
    job_id: string | null;
    kind: 'application' | 'interest';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLICANT_REVIEW_UNDONE, { ...args });
  };

  const onJobApplicantReviewFailed = (args: {
    team_id: string;
    job_id: string | null;
    kind: 'application' | 'interest';
    action: 'mark' | 'undo';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLICANT_REVIEW_FAILED, { ...args });
  };

  const onJobApplicantEmailClicked = (args: {
    team_id: string;
    job_id: string | null;
    kind: 'application' | 'interest';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_APPLICANT_EMAIL_CLICKED, { ...args });
  };

  const onJobHiringTabChanged = (args: {
    team_id: string;
    job_id: string | null;
    tab: 'applied' | 'interested' | 'suggested';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_HIRING_TAB_CHANGED, { ...args });
  };

  /* LAB-2771. `count` is how many people the tab showed, so the funnel can
     tell a role with suggestions from one without. LAB-2853: `source` is
     'email' when the lead arrived from a suggested-candidates email (its
     `utm_source`), else 'app'; `utm_content` says which link in it. */
  const onJobSuggestedCandidatesViewed = (args: {
    team_id: string;
    job_id: string | null;
    count: number;
    source: 'email' | 'app';
    utm_content: string | null;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_SUGGESTED_CANDIDATES_VIEWED, { ...args });
  };

  const onJobSuggestedCandidateOpened = (args: {
    team_id: string;
    job_id: string | null;
    rank: number;
    label: string;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_SUGGESTED_CANDIDATE_OPENED, { ...args });
  };

  /* LAB-2789: `interested` compares the contact rate of members who said
     "I'm interested" with the other suggestions. LAB-2847: fires when the
     server accepted a message sent through the platform (not on a 409). */
  const onJobSuggestedCandidateContacted = (args: {
    team_id: string;
    job_id: string | null;
    rank: number;
    label: string;
    interested: boolean;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_SUGGESTED_CANDIDATE_CONTACTED, { ...args });
  };

  /* LAB-2847: a lead saved which of the role's requirements count, after the
     server accepted it. */
  const onJobSuggestedCriteriaEdited = (args: {
    team_id: string;
    job_id: string | null;
    enabled_count: number;
    disabled_count: number;
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_SUGGESTED_CRITERIA_EDITED, { ...args });
  };

  const onJobSaved = (args: { job_id: string; team_id: string; source: JobSurface }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_SAVED, { ...args });
  };

  const onJobUnsaved = (args: { job_id: string; team_id: string; source: JobSurface }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_UNSAVED, { ...args });
  };

  const onJobSaveFailed = (args: {
    job_id: string;
    team_id: string;
    source: JobSurface;
    action: 'save' | 'unsave';
  }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOB_SAVE_FAILED, { ...args });
  };

  const onJobsSavedFilterApplied = (args: { filter_state: FilterStateParam }) => {
    captureEvent(JOBS_ANALYTICS.ON_JOBS_SAVED_FILTER_APPLIED, { ...args });
  };

  return {
    onJobsPageViewed,
    onJobsFiltersApplied,
    onJobsFiltersCleared,
    onJobsSearched,
    onJobsSortChanged,
    onJobClicked,
    onJobAlertCtaViewed,
    onJobAlertCtaClicked,
    onJobAlertSet,
    onJobAlertUpdated,
    onJobAlertConflict,
    onJobAlertRenamed,
    onJobAlertDeleted,
    onJobAlertEmailLinkClicked,
    onJobReferClicked,
    onJobReferModalOpened,
    onJobReferModalCancelled,
    onJobReferRefereeSelected,
    onJobReferRecipientsChanged,
    onJobReferNoteEdited,
    onJobReferNoteReset,
    onJobReferSuggestedLeadsShown,
    onJobReferSuggestedLeadSelected,
    onJobReferCcReferredPersonToggled,
    onJobReferSubmitted,
    onJobReferSucceeded,
    onJobReferFailed,
    onJobReferShareMenuOpened,
    onJobReferShared,
    onJobReferShareLinkOpened,
    onJobEmailProfileLinkClicked,
    onJobApplyClicked,
    onJobApplySignUpSubmitted,
    onJobApplySignUpFailed,
    onJobAspirantSignUpSubmitted,
    onJobDetailOpened,
    onJobApplyDrawerOpened,
    onJobApplyDrawerSaved,
    onJobApplySubmitted,
    onJobApplyFailed,
    onJobApplyStepViewed,
    onJobApplyFlowClosed,
    onJobApplyExternalRedirected,
    onJobUnlockInfoOpened,
    onJobCreateProfileClicked,
    onJobInterestComposerOpened,
    onJobInterestComposerCancelled,
    onJobInterestMarked,
    onJobInterestUndone,
    onJobInterestFailed,
    onTeamInterestMarked,
    onTeamInterestFailed,
    onJobApplyFollowDeclined,
    onJobInterestFollowDeclined,
    onJobApplyFollowFailed,
    onJobHiringViewed,
    onJobApplicantOpened,
    onJobApplicantReviewed,
    onJobApplicantReviewUndone,
    onJobApplicantReviewFailed,
    onJobApplicantEmailClicked,
    onJobHiringTabChanged,
    onJobSuggestedCandidatesViewed,
    onJobSuggestedCandidateOpened,
    onJobSuggestedCandidateContacted,
    onJobSuggestedCriteriaEdited,
    onJobSaved,
    onJobUnsaved,
    onJobSaveFailed,
    onJobsSavedFilterApplied,
  };
};
